export const ONBOARDING_CHECKLIST_KEY = 'Onboarding';
export const TRANSITION_CHECKLIST_KEY = 'Transition';
export const ADDON_CHECKLIST_KEY = 'AddOn';

export const ANSWER_QUESTIONS_KEY = 'Answer Practice Questions';
export const BOOKMARK_TOPIC_KEY = 'Bookmark a Topic';
export const COMPLETE_ICS_KEY = 'Complete Interactive Case Study';
export const COMPLETE_SIM_KEY = 'Complete Mini Sim Test';
export const COMPLETE_TOOLTIPS_TOUR_KEY = 'Complete Tooltips Tour';
export const CREATE_ACCOUNT_KEY = 'Create Account';
export const CREATE_CUSTOM_QUIZ_KEY = 'Create Custom Quiz';
export const REVIEW_POP_KEY = 'Review Possibility of Passing';
export const TOPIC_SEARCH_KEY = 'Search for a Topic';
export const SET_GOAL_KEY = 'Set Study Goal';
export const UNLOCK_MEMBERSHIP_KEY = 'Unlock Membership';
export const VIEW_BOOKMARKS_KEY = 'View Bookmarks';

export function hasChecklist(store, checklist) {
  // if there's no checklist set up for classification and context, return false
  if (!checklist) return false;

  // if there's no checklist state that is incomplete, return false
  const checklistState = store.getters['checklistStates/get'][checklist.id];
  if (!checklistState || checklistState.completed) return false;

  // this is a failsafe check for making sure the user hasn't completed the checklist
  // the only time this would happen is if a checklist item is deleted in the CMS and a
  // user only has that checklist item left
  const completedChecklistItemIds = getActiveCompletedChecklistItemIds(checklist, checklistState);
  if (completedChecklistItemIds.length >= checklist.checklistItems.length) {
    store.dispatch('checklistStates/create', [{ checklistId: checklist.id, completed: true }]);
    return false;
  }

  // all conditions are met to show checklist
  return true;
}

export function setupChecklist(store, checklistType) {
  const checklist = getChecklist(store, checklistType);

  if (checklist) {
    const completedChecklistItemIds = [];
    const createAccountItem = checklist.checklistItems.find(ci => ci.action === CREATE_ACCOUNT_KEY);
    if (createAccountItem) {
      completedChecklistItemIds.push(createAccountItem.id);
    }

    const state = {
      checklistId: checklist.id,
      completed: false,
      completedChecklistItemIds,
    };

    store.dispatch('checklistStates/create', [state]);
  }
}

export function getChecklist(store, checklistType) {
  const user = store.getters['user/get'];
  const allChecklists = store.getters['checklists/all'];
  return allChecklists.find(checklist => checklist.context === checklistType && checklist.classificationId === user.classificationId);
}

export function setupAddonChecklists(store) {
  const checklists = getChecklists(store, { includeNullClassification: true, context: ADDON_CHECKLIST_KEY });
  const params = [];
  checklists.forEach(checklist => {
    const completedChecklistItemIds = [];
    const createAccountItem = checklist.checklistItems.find(ci => ci.action === CREATE_ACCOUNT_KEY);
    if (createAccountItem) {
      completedChecklistItemIds.push(createAccountItem.id);
    }

    params.push({
      checklistId: checklist.id,
      completed: false,
      completedChecklistItemIds,
    });
  });

  if (params.length > 0) {
    store.dispatch('checklistStates/create', params);
  }
}

export function setupTransitionChecklists(store) {
  const checklists = getChecklists(store, { includeNullClassification: false, context: TRANSITION_CHECKLIST_KEY });
  const params = [];
  checklists.forEach(checklist => {
    const completedChecklistItemIds = [];
    const createAccountItem = checklist.checklistItems.find(ci => ci.action === CREATE_ACCOUNT_KEY);
    if (createAccountItem) {
      completedChecklistItemIds.push(createAccountItem.id);
    }

    params.push({
      checklistId: checklist.id,
      completed: false,
      completedChecklistItemIds,
    });
  });

  if (params.length > 0) {
    store.dispatch('checklistStates/create', params);
  }
}

function getChecklists(store, options = { includeNullClassification: true, context: undefined }) {
  const allChecklists = store.getters['checklists/all'];
  const classificationId = store.getters['user/get'].classificationId;
  return allChecklists.filter(c => {
    // matchesContext is an optional param so we'll set it to true by default
    let matchesContext = true;
    let classificationCheck = c.classificationId === classificationId;
    if (options.context) {
      matchesContext = options.context === c.context;
    }
    if (options.includeNullClassification) {
      classificationCheck = !c.classificationId || classificationCheck;
    }
    return matchesContext && classificationCheck;
  });
}

export function getWidgetChecklist(store, widget) {
  if (!widget) {
    return;
  }

  const checklistIdPriority = widget.config?.checklistIdPriority || [];
  const allChecklists = store.getters['checklists/all'];
  const user = store.getters['user/get'];

  let checklist;
  for (const checklistId of checklistIdPriority) {
    const clist = allChecklists.find(c => {
      const matchedList = c.id === checklistId;
      const noClassification = matchedList && !c.classificationId;
      const classificationMatchesUser = matchedList && c.classificationId === user?.classificationId;
      if (noClassification || classificationMatchesUser) {
        return c;
      }
    });
    if (!clist) {
      continue;
    }
    const checklistState = store.getters['checklistStates/get'][clist.id];
    if (!checklistState || checklistState?.completed) {
      continue;
    }

    checklist = clist;
    break;
  }
  return checklist;
}

export function getActiveCompletedChecklistItemIds(checklist, checklistState) {
  const checklistItemIds = checklist.checklistItems.map(item => item.id);
  return checklistItemIds.filter(id => checklistState.completedChecklistItemIds.includes(id));
}

export function setChecklistData(store, key, notify, value) {
  const checklists = getChecklists(store);
  checklists.forEach(checklist => {
    const checklistState = store.getters['checklistStates/get'][checklist.id];
    if (!checklistState) return;

    const checklistItems = checklist.checklistItems.filter(item => item.action === key);
    checklistItems.forEach(checklistItem => {
      // if already done with item or checklist, move on
      if (checklistState.completed || checklistState.completedChecklistItemIds.includes(checklistItem.id)) return;

      // if user made progress on item but didn't complete, just return
      if (checklistItem.value !== null &&  value < checklistItem.value) return;

      const completedChecklistItemIds = checklistState ? [...new Set([...checklistState.completedChecklistItemIds, checklistItem.id])] : [checklistItem.id];
      const completed = completedChecklistItemIds.length === checklist.checklistItems.length;

      const state = {
        checklistId: checklist.id,
        completed,
        completedChecklistItemIds,
      };

      store.dispatch('checklistStates/create', [state]);

      if (notify) {
        const message = `${checklistItem.displayName}: Complete`;
        setTimeout(() => notify({
          group: 'notifications',
          text: message,
          type: 'success',
        }), 1500);
      }
    });
  });
}
